// ---------------------------------------------------------------------------
// V23a — WHAT THE BOARD SAYS ABOUT PUBLICATION.
//
// `18-client-portal.md` §4's S2 rule, applied one surface earlier: "the client always
// knows which of the two things they are looking at". A scheduler needs the same
// certainty in the other direction — whether what they are moving is visible to the
// client yet.
//
// PURE, AND IN THE NODE TEST BUILD, for system/board.ts's reason: almost nothing that
// makes this right or wrong is visual. There are three states and the middle one is the
// one a screen gets wrong.
// ---------------------------------------------------------------------------

/** What the roster-versions endpoint says about an event. */
import { english, type Translate } from "../i18n/translate";

export type RosterPublication = {
  has_unpublished_changes: boolean;
  current_version_id: string | null;
  may_publish: boolean;
  versions: readonly { version_id: string; version_no: number; published_at: string }[];
};

export type PublicationStrip = {
  /** `live` published and current · `attention` published and behind · `neutral` never.
   *  The design system's four tone names, so the screen maps nothing. */
  tone: "live" | "attention" | "neutral";
  /** The short label beside the icon. Never a colour, never a bare number. */
  label: string;
  /** One sentence. Says what the CLIENT can see, which is the question being asked. */
  sentence: string;
  /** Whether the publish control does anything if pressed. */
  publishable: boolean;
};

/**
 * NEVER PUBLISHED IS NOT "OUT OF DATE", and collapsing the two is the defect this
 * function exists to prevent. A roster that has never been published shows the client
 * nothing at all; a roster that is behind shows them something real and slightly old.
 * They are opposite risks and an amber chip on both would hide the worse one.
 */
export function publicationStrip(p: RosterPublication | null, t: Translate = english): PublicationStrip {
  if (p === null) {
    return {
      tone: "neutral",
      label: t("Publication unknown"),
      // HONEST RATHER THAN REASSURING. §7.3's principle, one screen early: a failed read
      // must never render as a confident answer.
      sentence:
        t("We could not read this event's publication state, so this board cannot say what the client can see. Nothing below has been guessed."),
      publishable: false,
    };
  }

  const latest = p.versions[0];

  if (latest === undefined || p.current_version_id === null) {
    return {
      tone: "neutral",
      label: t("Not published"),
      sentence: t("The client cannot see this roster yet. Publishing shows them the posts and the crew as they stand now."),
      publishable: p.may_publish,
    };
  }

  if (p.has_unpublished_changes) {
    return {
      tone: "attention",
      label: t("Version {n}, behind", { n: latest.version_no }),
      sentence: t("The client is looking at version {n}. Changes made since then are not visible to them until you publish again.", { n: latest.version_no }),
      publishable: p.may_publish,
    };
  }

  return {
    tone: "live",
    label: t("Version {n}", { n: latest.version_no }),
    sentence: t("The client is looking at version {n}, which matches this board.", { n: latest.version_no }),
    // NOTHING TO PUBLISH IS NOT A PERMISSION PROBLEM, and the database refuses it too —
    // a version identical to the one before it is noise on the client's changes screen.
    publishable: false,
  };
}
